<template>
  <v-main class="pt-7 px-3 bg-default">
    <div class="d-flex align-center justify-center mb-6 relative-title">
      <v-btn small fab color="tertiary" dark elevation="0" class="back-icon" @click="goToDashboard">
        <v-icon color="primary">mdi-chevron-left</v-icon>
      </v-btn>
      <div class="page-title secondary-font">Meus EPI's</div>
    </div>

    <div v-if="loading" class="text-center mt-10">
      <v-progress-circular indeterminate color="primary" size="50" />
    </div>

    <template v-else>
      <template v-if="entregas.length === 0">
        <h3 class="text-center secondary-font mt-8 grey--text text--darken-1">Nenhuma entrega de EPI encontrada.</h3>
      </template>

      <template v-else>
        <div v-if="entregasConfirmaveis.length > 0" class="mb-8">
          <div class="px-1 mb-2 mt-2">
            <h3 class="secondary-font error--text text--darken-1">
              <v-icon color="error" class="mr-1 mb-1">mdi-alert-circle-outline</v-icon>
              Pendentes de Assinatura
            </h3>
          </div>

          <v-row align="center" class="mb-2 px-1">
            <v-col cols="auto">
              <v-checkbox
                v-model="selecionarTodos"
                hide-details
                class="mt-0 pt-0"
                color="primary"
                label="Selecionar todos pendentes"
                @change="toggleSelecionarTodos"
              />
            </v-col>
            <v-col class="text-right body-2 grey--text font-weight-bold">
              {{ selecionados.length }} selecionado(s)
            </v-col>
          </v-row>

          <v-row>
            <v-col v-for="item in entregasConfirmaveis" :key="item.id" cols="12" sm="6" lg="4">
              <EntregaEpiCard
                :entrega="item"
                :selectable="true"
                :selected="selecionados.includes(item.id)"
                @toggle-select="toggleSelecionado(item.id)"
              />
            </v-col>
          </v-row>

          <v-row no-gutters justify="center" class="mt-6">
            <v-btn
              color="primary"
              class="secondary-font text-capitalize font-weight-bold py-7 px-10"
              elevation="6"
              rounded
              x-large
              :disabled="selecionados.length === 0 || confirming"
              :loading="confirming"
              @click="abrirEntregaConfirmacaoEpi"
            >
              Confirmar Recebimento ({{ selecionados.length }})
            </v-btn>
          </v-row>
        </div>

        <v-divider v-if="entregasConfirmaveis.length > 0 && entregasConfirmadas.length > 0" class="my-8"></v-divider>

        <div v-if="entregasConfirmadas.length > 0" class="mb-6">
          <div class="px-1 mb-4">
            <h3 class="secondary-font success--text text--darken-2">
              <v-icon color="success" class="mr-1 mb-1">mdi-check-circle-outline</v-icon>
              Histórico de EPIs Assinados
            </h3>
          </div>

          <v-row>
            <v-col v-for="item in entregasConfirmadas" :key="item.id" cols="12" sm="6" lg="4">
              <EntregaEpiCard :entrega="item" :selectable="false" :selected="false" />
            </v-col>
          </v-row>
        </div>
      </template>
    </template>

    <EntregaConfirmacaoEpi
      v-if="showModalConfirmacao"
      v-model="showModalConfirmacao"
      :epis="episSelecionadosObjetos"
      :assinaturaAtual="assinaturaUsuario"
      :loading="confirming"
      @nova-assinatura="aoSalvarAssinatura"
      @confirmar="confirmarRecebimento"
    />
  </v-main>
</template>

<script>
import EntregaEpiCard from '@/modules/EntregasEpi/EntregaEpiCard.vue'
import EntregaConfirmacaoEpi from './EntregaConfirmacaoEpi.vue'
import entregaEpiService from '@/services/entregaEpi'
import assinaturaService from '@/services/assinatura'

export default {
  name: 'EntregasEpi',
  components: {
    EntregaEpiCard,
    EntregaConfirmacaoEpi
  },
  data() {
    return {
      loading: false,
      confirming: false,
      entregas: [],
      selecionados: [],
      selecionarTodos: false,
      showModalConfirmacao: false,
      assinaturaUsuario: null
    }
  },
  computed: {
    entregasConfirmaveis() {
      return this.entregas.filter(entrega => this.podeConfirmar(entrega))
    },
    entregasConfirmadas() {
      return this.entregas.filter(entrega => !this.podeConfirmar(entrega))
    },
    episSelecionadosObjetos() {
      return this.entregas.filter(entrega => this.selecionados.includes(entrega.id))
    }
  },
  async mounted() {
    await this.carregarEntregas()
    await this.buscarAssinatura()
  },
  methods: {
    podeConfirmar(entrega) {
      return !entrega.dtConfirmacaoEntrega
    },
    goToDashboard() {
      this.$router.push({ name: 'dashboard' })
    },
    async carregarEntregas() {
      this.loading = true
      try {
        const { data } = await entregaEpiService.listar()
        this.entregas = data.data || []
        this.selecionados = this.selecionados.filter(id => this.entregasConfirmaveis.some(entrega => entrega.id === id))
        this.selecionarTodos =
          this.entregasConfirmaveis.length > 0 && this.selecionados.length === this.entregasConfirmaveis.length
      } catch ({ response }) {
        this.$showErrorMessage(response?.data?.message || 'Erro ao carregar entregas de EPI')
      } finally {
        this.loading = false
      }
    },
    toggleSelecionado(id) {
      const entrega = this.entregas.find(item => item.id === id)
      if (!entrega || !this.podeConfirmar(entrega)) return

      if (this.selecionados.includes(id)) {
        this.selecionados = this.selecionados.filter(itemId => itemId !== id)
      } else {
        this.selecionados = [...this.selecionados, id]
      }

      this.selecionarTodos =
        this.entregasConfirmaveis.length > 0 && this.selecionados.length === this.entregasConfirmaveis.length
    },
    toggleSelecionarTodos(value) {
      if (value) {
        this.selecionados = this.entregasConfirmaveis.map(entrega => entrega.id)
        return
      }
      this.selecionados = []
    },

    async buscarAssinatura() {
      try {
        const { data } = await assinaturaService.buscaAssinatura()
        if (data && data.assinatura) {
          this.assinaturaUsuario = data.assinatura
        } else {
          this.assinaturaUsuario = null
        }
      } catch (error) {
        this.assinaturaUsuario = null
      }
    },

    abrirEntregaConfirmacaoEpi() {
      this.showModalConfirmacao = true
    },

    async aoSalvarAssinatura() {
      this.$showLoading()
      ;(await this.buscarAssinatura()) / this.$hideLoading()
    },

    async confirmarRecebimento() {
      if (!this.selecionados.length || !this.assinaturaUsuario) return

      this.confirming = true

      try {
        const { data } = await entregaEpiService.confirmar(this.selecionados, this.assinaturaUsuario)

        this.$showSuccessMessage(data?.message || 'Recebimento confirmado com sucesso!')
        this.selecionados = []
        this.selecionarTodos = false
        this.showModalConfirmacao = false
        await this.carregarEntregas()
      } catch ({ response }) {
        this.$showErrorMessage(response?.data?.message || 'Erro ao confirmar recebimento')
      } finally {
        this.confirming = false
      }
    }
  }
}
</script>

<style scoped>
.relative-title {
  position: relative;
  width: 100%;
}

.page-title {
  font-size: 1.25rem;
}

.back-icon {
  position: absolute;
  left: 0;
}
</style>
